import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// redux
import { SET_SCANKIT_IMAGE_LIST, SET_SCANKIT_TAG_LIST, TOGGLE_SWITCH } from "redux/types";

// styles
import { StyledViewer } from "./StyledViewer";

// components
import Image360 from "components/Image360/Image360";
import { getModelBackground, toCssBackground } from "utils/background";
import ScanViewerTagMenu from "components/ScanViewerTagMenu/ScanViewerTagMenu";

// api
import { getObjectInfo } from "api/scankit/getObjectInfo.api";

// utils
import { getVisitorId, getVisitId } from "utils/visitIdentity";

// images
import Image360Logo from "./images/360logo.png";
import { ReactComponent as ZoomIn } from "./images/zoom-in.svg";
import { ReactComponent as ZoomOut } from "./images/zoom-out.svg";
import { ReactComponent as SwitchHorizontal } from "./images/switch-horizontal.svg";

// icons
import { CgEye } from "react-icons/cg";
import { RiEyeCloseLine } from "react-icons/ri";
import { BsListUl } from "react-icons/bs";
import SERVER from "constants/server";

// i18n
import { useTranslation } from "react-i18next";

const ScanKitViewer = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const imageList = useSelector((store) => store.imageList.scanFileList);
  const tags = useSelector((store) => store.tagList.oldTags);

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const uuid = urlParams.get("uid");
  // 帶 fullscreen=true 時，圖片等比例放大到貼齊畫面（不裁切），未帶則維持原本行為
  const fullscreen = urlParams.get("fullscreen") === "true";
  // 帶 hideLogo=true 時，隱藏右上角的 360 圖標，其餘按鈕不受影響
  const hideLogo = urlParams.get("hideLogo") === "true";
  // 帶 hideTagMenu=true 時，不顯示標籤選單按鈕，其餘按鈕不受影響
  const hideTagMenu = urlParams.get("hideTagMenu") === "true";
  const [downloading, setDownloading] = useState(false);
  const [viewer, setViewer] = useState(false);
  // API 失敗時的後端錯誤碼；null 代表正常。以前失敗會直接留白，只剩 360 圖標
  const [loadError, setLoadError] = useState(null);
  // 後端錯誤碼 → 顯示文字。000038 擁有者方案到期或不可用、000013 本期瀏覽流量用罄；
  // 000006 未公開、000007 不存在；其他（含網路錯誤）一律顯示載入失敗
  const VIEWER_ERROR_KEY = {
    "000038": "viewer.unavailable",
    "000013": "viewer.quotaExceeded",
    "000006": "viewer.notFound",
    "000007": "viewer.notFound",
  };

  // 細節開關，預設值為editor編輯值
  const [currentProject, setCurrentProject] = useState({
    scanSwitch: true,
    tagSwitch: true,
  });

  // 標籤選單相關
  const [menuOpen, setMenuOpen] = useState(false);
  // 目前轉到第幾張（由 Image360 回拋），用來高亮「這張畫面上有的標籤」
  const [currentIndex, setCurrentIndex] = useState(0);
  // 拖曳中要讓抽屜穿透滑鼠事件，否則滑鼠掃過抽屜會把旋轉中斷掉
  const [dragging, setDragging] = useState(false);
  // 點選單項目要跳到哪個標籤。
  // 這個值一定要存成 state：Image360 是靠物件 identity 改變來觸發跳轉的，
  // 若在 JSX 裡 inline 建物件，每次 re-render 都會重跑，拖曳時畫面會被不斷拉回同一張。
  const [currentTag, setCurrentTag] = useState(null);

  const scaleRate = 0.1; // 縮放倍率
  const scaleLimit = 30; // 點擊次數限制（起始為0需要-1
  const [rate, setRate] = useState(1);
  const [zoomTimes, setZoomTimes] = useState(0);

  const handleSwitchEvent = () => {
    const uuid = new URLSearchParams(queryString).get("uid");
    if (uuid) {
      window.open(
        `${SERVER.VIEWER_URL}/?uid=${uuid}&modelIndex=1`,
        "_self",
        "noopener, noreferrer",
      );
    }
  };

  const handleZoomInEvent = () => {
    if (zoomTimes > scaleLimit) return;
    setRate(rate + scaleRate);
    setZoomTimes(zoomTimes + 1);
  };

  const handleZoomOutEvent = () => {
    if (zoomTimes < scaleLimit * -1 || rate <= 0.2) return;
    setRate(rate - scaleRate);
    setZoomTimes(zoomTimes - 1);
  };

  const handleMouseWheel = (event) => {
    // 標籤開關開啟及有多個標籤則不執行
    if (currentProject.tagSwitch && tags.length > 0) return;
    // 滾輪垂直距離超過120才觸發乙次縮放
    if (Math.abs(event.deltaY) > 10) {
      // 滾輪往上滾(數值為正)為放大，滾輪往下滾(數值為負)為縮小
      if (event.deltaY > 0) handleZoomInEvent();
      if (event.deltaY < 0) handleZoomOutEvent();
    }
  };

  const handleSwitchDetails = () => {
    setRate(1); // 使用細節功能時，不使用放大縮小功能，故恢復預設值
    setZoomTimes(0);
    // 標籤全部隱藏時，選單點了也不會有任何反應，直接收起來
    setMenuOpen(false);
    setCurrentProject({
      ...currentProject,
      tagSwitch: !currentProject.tagSwitch,
    });
    dispatch({ type: TOGGLE_SWITCH, payload: { tagSwitch: !currentProject.tagSwitch } });
  };

  // Image360 每換一張照片就會回拋目前張數
  const handleIndexChange = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleSelectTag = useCallback((serialNumber, tag) => {
    setCurrentTag({ serialNumber, imageIndex: tag.scankitIndex });
    // 手機畫面小，跳過去之後把抽屜收起來，否則剛展開的標籤面板會被清單擋住
    if (window.matchMedia("(max-width: 414px)").matches) setMenuOpen(false);
  }, []);

  // 開始拖曳旋轉。Image360 在這個時間點也會把展開中的標籤面板關掉，
  // 這裡一併把 currentTag 清掉，下次點同一個標籤才會重新展開而不是被 toggle 成收合。
  const handleDragStart = () => {
    setDragging(true);
    setCurrentTag(null);
  };

  const handleDragEnd = () => {
    setDragging(false);
  };

  // scankitIndex 若不是整數或超出照片張數，跳過去會讓所有照片都被隱藏（整個畫面空白），所以不列出來。
  // 一定要先檢查是整數：null、"1" 這類值用 >= / < 比較會被轉型而通過，但 Image360 是用 === 比對。
  // serialNumber 沿用原始索引 +1，才會跟畫面上 marker 的編號對得起來。
  const menuItems = useMemo(
    () =>
      tags
        .map((tag, index) => ({ tag, serialNumber: index + 1 }))
        .filter(
          ({ tag }) =>
            Number.isInteger(tag.scankitIndex) &&
            tag.scankitIndex >= 0 &&
            tag.scankitIndex < imageList.length,
        ),
    [tags, imageList],
  );

  const [showSwitchButton, setShowSwitchButton] = useState(false);
  // 物件的背景設定（照片這個模型）；360 照片本身是 JPG，目前背景只出現在照片外圍
  const [pageBackground, setPageBackground] = useState(undefined);
  useEffect(() => {
    if (!uuid) return false;

    setDownloading(true);
    // 帶上訪客識別，後端依此記錄閱覽並判斷新訪客 / 回訪訪客（AM-185）
    getObjectInfo(uuid, { guest: getVisitorId(), visitId: getVisitId() })
      .then((res) => {
        const onlineProductModels =
          res?.onlineProduct?.onlineProductEditors?.[0]?.onlineProductModels ?? [];

        const firstModel = onlineProductModels[0] || {};

        const has3DGSObject = onlineProductModels.some(
          ({ splatDnsSrc, splatSrc, lastUploadFileType }) =>
            (splatDnsSrc || splatSrc) && lastUploadFileType?.includes("splat"),
        );
        setShowSwitchButton(has3DGSObject || false);
        setPageBackground(toCssBackground(getModelBackground(firstModel)));
        setCurrentProject({
          ...currentProject,
          scanSwitch: firstModel.scanSwitch,
          tagSwitch: firstModel.tagSwitch,
        });
        dispatch({
          type: SET_SCANKIT_IMAGE_LIST,
          payload: {
            scanFileList: firstModel.scanFileList,
          },
        });
        dispatch({
          type: SET_SCANKIT_TAG_LIST,
          payload: {
            // API 沒回這個欄位時要保底成空陣列，否則後面的 tags.length 會直接 throw
            oldTags: firstModel.onlineProductTags ?? [],
          },
        });
        dispatch({
          type: TOGGLE_SWITCH,
          payload: {
            scanSwitch: firstModel.scanSwitch,
            tagSwitch: firstModel.tagSwitch,
          },
        });
      })
      .catch((err) => {
        // 用 || 而不是 ??：後端若回空字串也要算失敗，不能掉進 Image360 分支
        setLoadError(err?.response?.data?.code || "unknown");
      })
      .finally(() => {
        setDownloading(false);
        setViewer(true);
      });
  }, []);

  return (
    <StyledViewer
      style={pageBackground ? { background: pageBackground } : undefined}
      onMouseDown={handleDragStart}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
    >
      {tags.length > 0 && (
        <div className="viewerControls">
          {!hideTagMenu && menuItems.length > 0 && (
            <button
              type="button"
              className={menuOpen ? "controlButton controlActive" : "controlButton"}
              aria-label={t("scankitViewer.openTagList")}
              aria-expanded={menuOpen}
              // 標籤全部隱藏時選單沒有意義，但保留按鈕（不抽掉）避免下方的眼睛按鈕位移，
              // 同時讓使用者知道這個物件其實是有標籤的
              disabled={!currentProject.tagSwitch}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <BsListUl />
              <span className="controlCount">{menuItems.length}</span>
            </button>
          )}
          <button
            type="button"
            className={currentProject.tagSwitch ? "controlButton" : "controlButton controlOff"}
            aria-label={t("scankitViewer.toggleTag")}
            aria-pressed={currentProject.tagSwitch}
            onClick={handleSwitchDetails}
          >
            {currentProject.tagSwitch ? <CgEye /> : <RiEyeCloseLine />}
          </button>
        </div>
      )}
      {!hideTagMenu && menuItems.length > 0 && currentProject.tagSwitch && (
        <ScanViewerTagMenu
          items={menuItems}
          imageList={imageList}
          open={menuOpen}
          currentIndex={currentIndex}
          dragging={dragging}
          fullscreen={fullscreen}
          onClose={handleCloseMenu}
          onSelect={handleSelectTag}
        />
      )}
      {!hideLogo && (
        <div className="scankitViewerLogo">
          <img src={Image360Logo} alt="360-logo" />
        </div>
      )}

      <div className="actionButtons">
        {showSwitchButton && (
          <div onClick={handleSwitchEvent} className="zoomHover">
            <SwitchHorizontal />
          </div>
        )}
        {(!currentProject.tagSwitch || tags.length === 0) && (
          <>
            <div
              onClick={handleZoomInEvent}
              className={zoomTimes > scaleLimit ? "zoomDisabled" : "zoomHover"}
            >
              <ZoomIn className="zoomIcon" />
            </div>
            <div
              onClick={handleZoomOutEvent}
              className={rate <= 0.2 ? "zoomDisabled" : "zoomHover"}
            >
              <ZoomOut className="zoomIcon" />
            </div>
          </>
        )}
      </div>

      <div className="contentContainer" onWheel={handleMouseWheel}>
        {downloading ? (
          <div className="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        ) : loadError !== null ? (
          <div
            className="viewerMessage"
            role="alert"
            style={{
              padding: "0 24px",
              textAlign: "center",
              color: "#666",
              fontSize: "16px",
              lineHeight: 1.6,
            }}
          >
            {t(VIEWER_ERROR_KEY[loadError] ?? "viewer.loadFailed")}
          </div>
        ) : (
          <Image360
            imageList={imageList}
            viewer={viewer}
            rate={rate}
            tags={tags}
            tagNavbar={false}
            fullscreen={fullscreen}
            currentTag={currentTag}
            onIndexChange={handleIndexChange}
          />
        )}
      </div>
    </StyledViewer>
  );
};

export default ScanKitViewer;
